import styled from '@emotion/styled';
import { Button } from 'antd';
import FormField from 'components/FormField';
import { getStoreCount, StoreScopesModal } from 'components/StoreScopes';
import useModal from 'hooks/useModal';
import { useTranslate } from '@tolgee/react';
import { useFormikContext } from 'formik';
import { FV } from '../NewPolicy/share';
import PopoverPreview from 'components/StoreScopes/PopoverPreview';
import { useMemo } from 'react';

export const Content = styled.div`
  display: flex;
  align-items: center;
  > div {
    margin-right: 8px;
  }
  > button {
    padding-left: 0;
  }
`;

export interface StoreScopesFieldProps {
  disabled?: boolean;
  platform: General.PlatformUpperCase;
  permId: General.PermissionId;
}

export const ALL2all = (scopes: FV['regionStoreList']) => {
  return scopes
    ? scopes.map<General.StoreScope['regionStoreScopes'][number]>((scope) => {
        const { region, ...others } = scope;

        if (region === 'ALL') {
          return {
            ...others,
            region: 'all',
          };
        }

        return {
          region,
          ...others,
        };
      })
    : [];
};

export const all2ALL = (scopes: General.StoreScope['regionStoreScopes']) => {
  return scopes.map<Required<FV>['regionStoreList'][number]>((scope) => {
    const { region, ...others } = scope;

    if (region === 'all') {
      return {
        ...others,
        region: 'ALL',
      };
    }

    return {
      region,
      ...others,
    };
  });
};

function StoreScopesField(props: StoreScopesFieldProps) {
  const { disabled: disabledProp = false, platform, permId } = props;

  const { t } = useTranslate();

  const {
    values: { knowledgeId },
  } = useFormikContext<FV>();

  const disabled = disabledProp || Boolean(knowledgeId);

  const { visible, show, hide } = useModal();

  const getCount = (value: FV['regionStoreList']) =>
    getStoreCount([
      {
        platform,
        regionStoreScopes: ALL2all(value),
        storeCount: 0,
      },
    ]);

  return (
    <FormField<FV['regionStoreList']>
      required
      name="regionStoreList"
      label={t('aide.应用店铺')}
      validate={(value) => (getCount(value) > 0 ? undefined : t('aide.请选择应用店铺'))}
    >
      {({ field: { value = [], onChange } }) => {
        const storeCount = getCount(value);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const scopes = useMemo(() => {
          return value.length === 0
            ? []
            : [
                {
                  platform,
                  regionStoreScopes: ALL2all(value),
                  storeCount,
                },
              ];
        }, [value, storeCount]);

        if (disabled) {
          return <PopoverPreview value={scopes} />;
        }

        const modal = (
          <StoreScopesModal
            platform={platform}
            permId={permId}
            visible={visible}
            showFooterTips={false}
            onCancel={hide}
            value={scopes}
            onChange={([{ regionStoreScopes = [] } = {}]) => onChange(all2ALL(regionStoreScopes))}
          />
        );

        if (storeCount === 0) {
          return (
            <Content>
              <Button disabled={disabled} type="link" onClick={show}>
                {t('remind.选择店铺')}
              </Button>
              {modal}
            </Content>
          );
        }

        return (
          <Content>
            <PopoverPreview value={scopes} />
            <Button disabled={disabled} type="link" onClick={show}>
              {t('phrase.重新选择')}
            </Button>
            {modal}
          </Content>
        );
      }}
    </FormField>
  );
}

export default StoreScopesField;
